import { FunctionalComponent, h } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import { loadMore } from "../auth/auth";
import { state } from "../stores/notification-handler-store";
import { state as userSettingsState } from "../stores/user-settings-handler-store";
import { INotification } from "@trumpf/notifies.notification-handler";
import { marked } from "marked";
import striptags from "striptags";
import dompurify from "dompurify";
import { getHumanTime } from "../../utils/getHumanTime";
import { TrumpfNotificationSeverity } from "../notification-item/notification-item";

export const Loading: FunctionalComponent = () => (
    <div class={"uxn-notification-center__loading-ct"}>
        <ux-spinner-overlay opened={true} />
    </div>
);

export const Error: FunctionalComponent<{
    message: string;
}> = ({ message }) => (
    <div class={"uxn-notification-center__error"}>
        <div class={"uxn-notification-center__error-message"}>{message}</div>
    </div>
);

export const NoToken: FunctionalComponent<{
    message: string;
}> = ({ message }) => (
    <div class={"uxn-notification-center__error"}>
        <div class={"uxn-notification-center__error-message"}>{message}</div>
    </div>
);

export const Header: FunctionalComponent<{
    settingsURL: string;
    settingsDisabled: boolean;
    notificationSettingsTarget: string;
}> = ({ settingsURL, settingsDisabled, notificationSettingsTarget }) => (
    <div class={"uxn-notification-center__header"}>
        {settingsURL && settingsDisabled !== true && (
            <a href={settingsURL} target={notificationSettingsTarget}>
                <ux-icon
                    id="notificationSettingsIcon"
                    name="cog"
                    weight="bold"
                />
            </a>
        )}
        <button class="button-close" data-ux-tooltip-close>
            <ux-icon name="close" />
        </button>
    </div>
);

export const NoMessages: FunctionalComponent = () => {
    const { t } = i18nProvider;

    return (
        <div class={"uxn-notification-center__empty"}>
            <ux-icon
                class={"uxn-notification-center__empty-icon"}
                name="no-notification"
            />
            <p class={"uxn-notification-center__empty-info"}>
                {t("notifications:noMessages")}
            </p>
        </div>
    );
};

export const ActivateSettingsPrompt: FunctionalComponent = () => {
    const { t } = i18nProvider;

    const noCategoriesChecked =
        userSettingsState.get("userSettings") &&
        !userSettingsState
            .get("categories")
            .categories.some((c) =>
                userSettingsState
                    .get("userSettings")
                    .categories.some(
                        (uc) =>
                            uc.name === c.name &&
                            uc.channels.find(
                                (channel) =>
                                    channel.name === "notificationChannel"
                            )?.enabled
                    )
            );

    if (noCategoriesChecked) {
        return (
            <div class={"uxn-notification-center__empty"}>
                <ux-icon
                    class={"uxn-notification-center__empty-icon"}
                    name="no-notification"
                />
                <p class={"uxn-notification-center__empty-info"}>
                    {t("notifications:activateInSettings")}
                </p>
            </div>
        );
    }

    return <NoMessages />;
};

export const NotificationItem: FunctionalComponent<{
    data: INotification;
    dateFormat: string;
    timeFormat: string;
}> = ({ data, dateFormat, timeFormat }) => {
    const { t } = i18nProvider;
    let message = data.message;
    const renderer = new marked.Renderer();
    renderer.link = (href, title, text) => {
        return `<a target="_blank" href="${href}"${
            title ? ` title="${title}"` : ""
        }>${text}</a>`;
    };
    renderer.paragraph = (text) => {
        return `<p style="margin: 0; padding: 0;">${text}</p>`;
    };
    message = marked.parse(message, { renderer });
    message = striptags(message, ["a"]);
    message = dompurify.sanitize(marked.parse(message, { renderer }), {
        ADD_ATTR: ["target"],
    });

    const willDeleteIn = getHumanTime((data.timestamp + data.ttl) * 1000);

    return (
        <uxn-notification-item
            source={
                data.category === "system-generated"
                    ? t("notifications:systemGenerated")
                    : userSettingsState
                          .get("categories")
                          ?.categories?.find(
                              (category) => category.name === data.category
                          )?.title
            }
            headline={data.subject}
            createdAt={data.timestamp.toString()}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            severity={(data.severity as unknown) as TrumpfNotificationSeverity}
            type="Overview"
            willDeleteIn={willDeleteIn}
            willDeleteInTooltip={t("notifications:autoDeleteAfterTime")}
        >
            <span slot="message" innerHTML={message} />
        </uxn-notification-item>
    );
};

export const NotificationCluster: FunctionalComponent<{
    type: string;
    headline: string;
}> = ({ type, headline }) => {
    const clusterDate = (type: string, date) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        const dateString = new Date(date).toDateString();

        if (type === "today") {
            return dateString === today.toDateString();
        }
        if (type === "yesterday") {
            return dateString === yesterday.toDateString();
        }
        if (type === "week") {
            return (
                dateString !== today.toDateString() &&
                dateString !== yesterday.toDateString()
            );
        }

        return false;
    };

    const filteredNotifications = state
        .get("notifications")
        .filter((notificationData) =>
            clusterDate(type, notificationData.timestamp * 1000)
        );

    if (filteredNotifications.length === 0) {
        return null;
    }

    return (
        <div
            class={`uxn-notification-center__cluster-container uxn-notification-center__cluster-container--${type}`}
        >
            {filteredNotifications.map((notificationData, index) => (
                <div
                    id={"notification-" + index}
                    class={"uxn-notification-center__notification-cluster-item"}
                >
                    <div class={"uxn-notification-center__cluster-headline"}>
                        <ux-tag>{headline}</ux-tag>
                    </div>
                    <NotificationItem
                        data={notificationData}
                        dateFormat="DD-MM-YYYY"
                        timeFormat="HH:mm"
                    />
                </div>
            ))}
        </div>
    );
};

export const Notifications: FunctionalComponent = () => {
    const { t } = i18nProvider;
    const anyNotifications = state.get("counter") > 0;

    if (anyNotifications) {
        return (
            <div class={"uxn-notification-center__notification-container"}>
                <NotificationCluster
                    type="today"
                    headline={t("notifications:todayLabel")}
                />
                <NotificationCluster
                    type="week"
                    headline={t("notifications:lastSevenDays")}
                />
                <NotificationCluster
                    type="olderThanAWeek"
                    headline={t("notifications:olderLabel")}
                />

                <ux-infinite-scroll-sensor
                    onUxInfiniteScrollSensorScrolledIntoView={() => loadMore()}
                />
            </div>
        );
    }

    return <ActivateSettingsPrompt />;
};

export const Body: FunctionalComponent = () => {
    const { t } = i18nProvider;
    const hasError = state.get("error") !== null;
    const isTokenSet = state.get("token") !== null;

    if (hasError) {
        return <Error message={t("notifications:error")} />;
    }

    if (isTokenSet) {
        return <Notifications />;
    }

    return <NoToken message={t("notifications:loginToView")} />;
};
